//  External libs
import React, { useEffect, useState } from "react";
import axiosInstance from '../../utils/axiosInstance.js';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { faCircleQuestion } from "@fortawesome/free-regular-svg-icons";
import { Tooltip } from "react-tooltip";
import { route_delete_device} from "../../constants/BackendRoutes.js";
import { toast } from "sonner";

// Assets
import "./popupPage.css";

// Components
import { route_update_device, route_add_device } from "../../constants/BackendRoutes.js";

const DeviceEditPopupForm = ({
  onClose,
  onSubmit,
  deviceTypes,
  inputData,
}) => {
  const heartbeatOptions = {
    "1 minute": 60,
    "10 minutes": 60 * 10,
    "1 hour": 60 * 60,
    "6 hours": 60 * 60 * 6,
    "12 hours": 60 * 60 * 12,
    "24 hours": 60 * 60 * 24,
  };

  const sandboxMode = {
    Production: false,
    Debug: true,
  };

  const debugMode = {
    Off: "OFF",
    Info: "INFO",
    Debug: "DEBUG",
    Trace: "TRACE",
  };

  const fields = [
    {
      key: "name",
      display: "Name",
      htmlType: "input",
      type: "string",
      default: "",
      tooltip: "Name of the device",
      validate: true,
      data: true,
    },
    {
      key: "uuid",
      display: "UUID",
      htmlType: "input",
      type: "hex",
      default: "",
      readOnly: "null",
      tooltip: "UUID of the device, must be 32 bit hex string",
      validate: true,
      data: false,
    },
    {
      key: "id",
      display: "ID",
      htmlType: "input",
      type: "string",
      default: "",
      readOnly: "true",
      hidden: "null",
      tooltip: "Autogenerated unique ID of the device, cannot be editted",
      data: false,
    },
    {
      key: "type",
      display: "Device Type",
      htmlType: "select",
      type: "list",
      list: deviceTypes,
      default: deviceTypes[0],
      tooltip: "Type of device, must match a schema",
      data: true,
    },
    {
      key: "heartbeat_int",
      display: "Heartbeat Interval",
      htmlType: "select",
      type: "enum",
      enum: heartbeatOptions,
      default: 60,
      tooltip: "The interval which the device has to report back to the server",
      data: true,
    },
    {
      key: "location",
      display: "Location [Latitude, Longitude]",
      htmlType: "array",
      type: "array",
      subtype: "latlong",
      length: 2,
      default: [0, 0],
      tooltip: "Location of the device",
      validate: true,
      data: true,
    },
    {
      key: "sandbox_mode",
      display: "Sandbox Mode",
      htmlType: "select",
      type: "enum",
      enum: sandboxMode,
      default: false,
      tooltip:
        "Sandbox mode of the device, debug enables more debugging features",
      data: true,
    },
    {
      key: "debug_mode",
      display: "Debug Mode",
      htmlType: "select",
      type: "enum",
      enum: debugMode,
      default: "OFF",
      tooltip: "Debug level of the console on the device",
      data: true,
    },
  ];
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const initFormData = {};
    const initErrorData = {};
    
    for (const field of fields) {
      switch (field.type) {
        case "string":
        case "hex":
          initFormData[field.key] = field.default ?? "";
          break;
        case "array":
        case "list":
          initFormData[field.key] = field.default ?? [];
          break;
        case "enum":
          initFormData[field.key] = getKeyByValue(field.enum, field.default);
          break;
        default:
          console.log("error, shouldnt be here");
          break;
      }
      initErrorData[field.key] = "";
    }
    setFormData(initFormData);
    setErrors(initErrorData);
  }, []);

  function getKeyByValue(object, value) {
    return Object.keys(object).find((key) => object[key] === value);
  }

  useEffect(() => {
    if (!inputData.device) {
        return;
    }
    const editData = {};
    for (const field of fields) {
      editData[field.key] = inputData.device[field.key] ?? field.default;
    }
    setFormData(editData);

  }, [inputData]);

  // Handle form input changes
  // It will update the formData state and clear any validation errors for the field being edited
  const handleChange = (e) => {
    const { name, value, id } = e.target;
    const fieldsIndex = fields.findIndex((field) => field.key === id);

    // newData will hold the updated value for the field being edited only
    let newData;
    
    if (fields[fieldsIndex].htmlType == "array") {
      // when it is array, the name will like "location-0"/"location-1"
      const index = Number(name.split("-")[1]);
      const newValue = [...formData[id]];

      // Update the specific index of the array with the new value
      newValue[index] = value;
      newData = { [id]: newValue };
    }
    else {
      newData = { [id]: value };
    }

    // prevData is previous state of formData, patching with newData with the field being edited        
    setFormData((prevData) => ({ ...prevData, ...newData }));

    // Clear validation error for the field being edited
    if (errors[id]) {
      setErrors((prevErrors) => ({ ...prevErrors, [id]: "" }));
    }
  };

  // Validate UUID
  const validate32CharHex = (uuid) => /^[0-9a-fA-F]{32}$/.test(uuid);

  function isLatitude(lat) {
    const val = parseFloat(lat);
    return isFinite(val) && Math.abs(val) <= 90;
  }

  function isLongitude(lng) {
    const val = parseFloat(lng);
    return isFinite(val) && Math.abs(val) <= 180;
  }

  const updateOrAddDevice = async (uuid, data, submitType) => {
    var request;
    if (submitType == "update") {
      request = `${route_update_device}/${uuid}`;
    } else if (submitType == "add") {
      request = `${route_add_device}/${uuid}`;
    } else {
      throw new Error("No submit type");
    }
    let json_data = JSON.stringify(data);
    let config = {
      method: "post",
      maxBodyLength: Infinity,
      url: request,
      headers: {
        "Content-Type": "application/json",
      },
      data: json_data,
    };
    try {
      const response = await axiosInstance.request(config);
      return response; // Ensure you return the response if needed
    } catch (error) {
      throw error; // Rethrow the error if needed
    }
  };

  const handleDeleteDevice = async (uuid) => {
      const isConfirmed = window.confirm(`Are you sure you want to delete the device ${uuid}?`);
  
      if (!isConfirmed) return; // Exit if the user cancels

      try {
          const url = `${route_delete_device}/${uuid}`;
          console.log("Deleting Device:", url);
          await axiosInstance.delete(url);
          toast.success(`Device ${uuid} deleted`);
      } catch (err) {
          console.error(err);
          toast.error(`Failed to delete device ${uuid}`);
      }
      onClose(); // Close the popup after deletion
  };  

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Collect form data with the field keys and values
    let consolidatedFormData = {};

    fields.forEach((field) => {
      switch (field.type) {
        case "string":
        case "hex":
        case "enum":
        case "list":
          consolidatedFormData[field.key] = formData[field.key];
          break;
        case "array":
          if (field.subtype === "latlong") {
            // html always retuns string, so convert to float for latlong
            consolidatedFormData[field.key] = formData[field.key].map(value => parseFloat(value));
          } else {
            consolidatedFormData[field.key] = formData[field.key];
          }
          break;
        default:
          break;
      }
    });

    // Validate form data and throw error if any field is invalid
    const newErrors = {};

    for (const field of fields) {
      if (field.validate) {
        switch (field.type) {
          case "string":
            if (!consolidatedFormData[field.key]) {
              newErrors[field.key] = "Name is required";
            }
            break;
          case "hex":
            if (!validate32CharHex(formData[field.key])) {
              newErrors[field.key] =
                `${field.display} must be a 32-character hexadecimal string`;
            }
            break;
          case "array":
            if (field.subtype == "latlong") {
              let error = "";
              if (!isLatitude(consolidatedFormData[field.key][0])) {
                error += "Latitude should be between -90 and +90. ";
              }
              if (!isLongitude(consolidatedFormData[field.key][1])) {
                error += "Latitude should be between -180 and +180. ";
              }
              if (error != "") {
                newErrors[field.key] = error;
              }
            }
            break;
          default:
            break;
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      console.log("Validation errors:", newErrors);
      setErrors(newErrors);
      return;
    }

    // Validation passed, prepare data to send to backend
    // Compare formData with inputData and prepare only the changed fields to be sent to the backend
    let htmlData = {};

    for (const field of fields) {
      if (field.data) {
        switch (field.type) {
          case "string":
          case "hex":
          case "list":
            if (
              inputData.device === undefined ||
              consolidatedFormData[field.key] != inputData.device[field.key]
            ) {
              htmlData[field.key] = consolidatedFormData[field.key];
            }
            break;
          case "array":
            if (
              inputData.device === undefined ||
              JSON.stringify(consolidatedFormData[field.key]) !=
                JSON.stringify(inputData.device[field.key])
            ) {
              htmlData[field.key] = consolidatedFormData[field.key];
            }
            break;
          case "enum":
            if (
              inputData.device === undefined ||
              field.enum[consolidatedFormData[field.key]] !=
                inputData.device[field.key]
            ) {
              htmlData[field.key] = field.enum[consolidatedFormData[field.key]];
            }
            break;
          default:
            break;
        }
      }
    }

    try {
      await updateOrAddDevice(
        consolidatedFormData.uuid,
        htmlData,
        inputData.submitType,
      );
    } catch (error) {
      console.log(error.response.data);
      console.log(error.response.status);
      newErrors.debug_mode = "An error occured on the server, please try again later...";
      setErrors(newErrors);
      return;
    }

    onSubmit(consolidatedFormData); // Pass validated form data to parent
    onClose(); // Close popup
  };

  const hideLabelIfNull = (field) => {
    if (field.hidden == "null") {
      if (formData[field.key] == "") {
        return { id: "hide" };
      }
    }
    return {};
  };

  const returnOptions = (field) => {
    if (field.type == "enum") {
      return Object.keys(field.enum).map((type, index) => {
        return (
          <option key={index} value={type}>
            {type}
          </option>
        );
      });
    } else if (field.type == "list") {
      return field.list.map((type, index) => {
        return (
          <option key={index} value={type}>
            {type}
          </option>
        );
      });
    }
    return null;
  };

  const readOnly = (field) => {
    if (field.readOnly == "true") {
      return true;
    } else if (field.readOnly == "null") {
      if (
        inputData != null &&
        inputData.device != null &&
        inputData.device[field.key] != ""
      ) {
        return true;
      }
    }
    return false;
  };

  const returnInput = (field) => {
    if (field.htmlType == "input") {
      return (
        <input
          type="text"
          name={field.key}
          id={field.key}
          value={formData[field.key]}
          onChange={handleChange}
          readOnly={readOnly(field)}
        />
      );
    } else if (field.htmlType == "select") {
      return (
        <select
          name={field.key}
          id={field.key}
          value={formData[field.key]}
          onChange={handleChange}
        >
          {returnOptions(field)}
        </select>
      );
    } else if (field.htmlType == "array") {
      const values = formData[field.key] || [];
      const length = values.length || 1;

      const arrayStyle = {
        width: `${88 / length}%`,
        marginRight: "2%",
      };
      const lastArrayStyle = {
        width: `${88 / length}%`,
      };
      return values.map((input, index) => (
        <input
            key={`${field.key}-${index}`}
            style={
                index === values.length - 1
                    ? lastArrayStyle
                    : arrayStyle
            }
            type="text"
            name={`${field.key}-${index}`}
            id={field.key}
            value={values[index]}
            onChange={handleChange}
        />
    ));
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup">
        <div class="popup-header">
          <span class="popup-title">{inputData.title}</span>
          <button class="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        {/* <div class="popup-header">
                    <span class="popup-title">Popup Title</span>
                    <button class="close-btn" onclick="closePopup()">×</button>
                </div> */}
        <form onSubmit={handleSubmit}>
          {fields.map((field) => (
            <label id={field.key} {...hideLabelIfNull(field)}>
              {field.display}
              <div style={{ marginLeft: "5px" }}>
                <FontAwesomeIcon
                  id={`${field.key}_tooltip`}
                  icon={faCircleQuestion}
                />
              </div>
              {returnInput(field)}
              {errors[field.key] && (
                <div className="error">{errors[field.key]}</div>
              )}
              <Tooltip
                anchorSelect={`#${field.key}_tooltip`}
                place="bottom"
                openOnClick="true"
              >
                {field.tooltip}
              </Tooltip>
            </label>
          ))}

          {/* Buttons */}
          <button className="popup-submit-btn" type="submit">
            Submit
          </button>
          <button  className="popup-submit-btn" type="button" 
            onClick={() => handleDeleteDevice(formData["uuid"])}>
            Delete
          </button>
        </form>
      </div>
    </div>
  );
};

export default DeviceEditPopupForm;
